import _ from "lodash";
import {
  Account,
  Authorization,
  AuthorizationType,
  PaOriginType,
  AuthorizationLog,
  AuthorizationStepKey,
} from "@samacare/graphql";
import { useHistory } from "react-router-dom";

import { useConfig, useFeatureFlag } from "../../hooks";
import {
  isTrackedPortalAuth,
  needCurrentUserAction,
  previousAuthorizationStatus,
} from "../../util/authUtils";
import { StatusCheckerErrorButton } from "../StatusCheckerErrorButton";
import { Alert, Button, Typography, Link } from "@samacare/design";
import VisibilityIcon from "@samacare/design/core/icons/Visibility";
import { interpolate } from "../../util/stringUtils";
import { UpdateAuthorizationStatusButton } from "@@components/UpdateAuthorizationStatusButton";
import { alpha, useTheme } from "@samacare/design/core/styles";
import { EnhancedServicesIcon } from "@samacare/component";
import WarningAmberIcon from "@samacare/design/core/icons/WarningAmberIcon";
import AlertTitle from "@samacare/design/core/AlertTitle";
import { cleanDrugName } from "../../../../server/src/shared_code";
import getAuthorizationRoute from "../../util/getAuthorizationRoute";
import * as stepUtils from "../../util/stepUtils";

export const CARD_PLACEMENT = {
  AUTH_LIST: "authList",
  CONTEXT_HEADER: "contextHeader",
  PATIENT_TREATMENT_DETAIL: "patientTreatmentDetail",
} as const;

type CardPlacement = (typeof CARD_PLACEMENT)[keyof typeof CARD_PLACEMENT];

export const ActionBanner = ({
  backgroundColor,
  color,
  icon,
  button,
  title,
  subtitle,
  subtitleVariant,
  dataCy,
  cardPlacement,
}: {
  backgroundColor?: string;
  color: string;
  icon?: React.ReactNode;
  button: React.ReactNode;
  title?: string;
  subtitle?: string | React.ReactNode;
  subtitleVariant?: "body1" | "subtitle2";
  dataCy: string;
  cardPlacement?: CardPlacement;
}) => {
  return (
    <Alert
      sx={{
        background: backgroundColor ?? null,
        color,
        margin:
          cardPlacement === CARD_PLACEMENT.CONTEXT_HEADER ? "5px 0px" : "5px",
        "& .MuiAlert-icon": {
          alignItems: "center",
        },
        "& .MuiAlert-action": {
          alignItems: "center",
        },
      }}
      severity="warning"
      icon={icon == null ? false : icon}
      action={button}
      data-cy={dataCy}
    >
      {title && <AlertTitle sx={{ margin: 0 }}>{title}</AlertTitle>}
      {subtitle && (
        <Typography variant={subtitleVariant ?? "body2"}>{subtitle}</Typography>
      )}
    </Alert>
  );
};

interface ActionBannersProps {
  authorization: Authorization;
  currentAccount: Account;
  cardPlacement?: CardPlacement;
}
export const ActionBanners: React.VFC<ActionBannersProps> = (props) => {
  const {
    authorization,
    currentAccount,
    cardPlacement = CARD_PLACEMENT.AUTH_LIST,
  } = props;

  const theme = useTheme();
  const config = useConfig();
  const history = useHistory();

  const { LAUNCH_DARKLY_FEATURE_FLAGS, AUTHORIZATION_STATUSES } =
    config.CONSTANTS;
  const paBoostDrugs = useFeatureFlag<
    { drugName: string; drugOptionId: number }[]
  >(LAUNCH_DARKLY_FEATURE_FLAGS.PaBoost);

  const paBoostEnabled = _.some(
    paBoostDrugs ?? [],
    (drug: { drugName: string; drugOptionId: number }) =>
      drug.drugOptionId === authorization.DrugOptionId
  );

  const previousStatus = previousAuthorizationStatus(
    authorization.authorizationLogs as AuthorizationLog[]
  );

  const displayActionRequiredBanner =
    authorization.status === AUTHORIZATION_STATUSES.ACTION_REQUIRED ||
    (authorization.status === AUTHORIZATION_STATUSES.EDIT_AND_RESUBMIT &&
      previousStatus === AUTHORIZATION_STATUSES.ACTION_REQUIRED);

  const getBannerPropsForActionRequired = () => ({
    backgroundColor: paBoostEnabled
      ? alpha(theme.palette.colors.C500, 0.12)
      : undefined,
    color: paBoostEnabled
      ? theme.palette.secondary.dark
      : theme.palette.warning.veryDark!,
    subtitle: paBoostEnabled
      ? "Review payer’s response."
      : "Response requires your review.",
    title: `New status from payer: Action required${
      paBoostEnabled
        ? ` for ${cleanDrugName(authorization?.drugOption?.drugName)}`
        : ""
    }`,
    icon: paBoostEnabled ? (
      <EnhancedServicesIcon sx={{ fontSize: "24px" }} />
    ) : (
      <WarningAmberIcon sx={{ fontSize: "24px" }} />
    ),
    buttonBackground: paBoostEnabled
      ? theme.palette.secondary.main
      : theme.palette.warning.main,
  });

  let portalUrl =
    authorization?.portal?.deepLink || authorization?.portal?.loginUrl;
  portalUrl = interpolate(portalUrl, authorization);

  return (
    <>
      {/**
       * If the auth API-tracked, do not display the action banner
       */}
      {isTrackedPortalAuth(authorization) &&
        config.CONSTANTS.PROGRESS_AUTHORIZATION_STATUSES.includes(
          authorization.status
        ) &&
        (authorization.portalAuthorizationId == null ||
          authorization.portalAuthorizationId === "") && (
          <ActionBanner
            backgroundColor={theme.palette.colors.P100}
            color={theme.palette.text.primary}
            icon={null}
            button={
              <UpdateAuthorizationStatusButton
                authorization={authorization}
                styleOverrides={{
                  width: "200px",
                  fontSize: "16px",
                  height: "35px",
                }}
              >
                Add ID
              </UpdateAuthorizationStatusButton>
            }
            subtitle="Please add authorization ID from the portal to enable tracking."
            subtitleVariant="body1"
            dataCy="addAuthorizationIdBanner"
          />
        )}

      {!_.isNil(authorization.PortalId) &&
        (authorization?.paOrigin !== PaOriginType.Copilot ||
          authorization?.portal?.isLegacy) &&
        authorization.formDetails?.currentStep != null &&
        authorization.formDetails?.currentStep >= 3 &&
        authorization.status ===
          config.CONSTANTS.AUTHORIZATION_STATUSES.PRESUBMISSION && (
          <ActionBanner
            backgroundColor={theme.palette.colors.P100}
            color={theme.palette.text.primary}
            icon={null}
            button={
              <UpdateAuthorizationStatusButton
                authorization={authorization}
                styleOverrides={{
                  width: "200px",
                  fontSize: "16px",
                  height: "35px",
                }}
              >
                Update
              </UpdateAuthorizationStatusButton>
            }
            subtitle="Please review the authorization in the portal and update the status accordingly."
            subtitleVariant="body1"
            dataCy="didYouSubmitThisBanner"
            cardPlacement={cardPlacement}
          />
        )}

      {authorization?.type === AuthorizationType.Portal &&
        authorization?.portal &&
        !authorization?.portal?.isFallback && (
          <ActionBanner
            backgroundColor={theme.palette.colors.LB50}
            color={theme.palette.text.primary}
            icon={null}
            button={
              <Link
                href={portalUrl}
                target="_blank"
                sx={{ color: "unset", textDecorationColor: "unset" }}
              >
                <Button
                  variant="outlined"
                  sx={{ backgroundColor: "#ffffff", pl: 1, pr: 1 }}
                  onClick={() =>
                    window.open(portalUrl, "_blank", "noopener noreferrer")
                  }
                >
                  <VisibilityIcon sx={{ mr: 1 }} />
                  Check Status in Portal
                </Button>
              </Link>
            }
            subtitle={
              <>
                For the most recent status, visit{" "}
                <Link
                  href={portalUrl}
                  target="_blank"
                  sx={{ color: "unset", textDecorationColor: "unset" }}
                >
                  payer’s website
                </Link>
                .
              </>
            }
            subtitleVariant="body1"
            dataCy="submittedViaPortalBanner"
            cardPlacement={cardPlacement}
          />
        )}

      {needCurrentUserAction(authorization, currentAccount) && (
        <ActionBanner
          backgroundColor={theme.palette.colors.P100}
          color={theme.palette.text.primary}
          icon={null}
          button={
            <StatusCheckerErrorButton
              key={`StatusCheckerErrorButton-id-${authorization.id}`}
              currentAccount={currentAccount}
              authorization={authorization}
              data-cy="resolveButton"
            >
              Resolve
            </StatusCheckerErrorButton>
          }
          subtitle="Unable to check status."
          subtitleVariant="body1"
          dataCy="loginErrorsBanner"
          cardPlacement={cardPlacement}
        />
      )}

      {displayActionRequiredBanner &&
        (() => {
          const {
            backgroundColor,
            color,
            subtitle,
            title,
            icon,
            buttonBackground,
          } = getBannerPropsForActionRequired();

          const button = () => {
            if (cardPlacement === CARD_PLACEMENT.CONTEXT_HEADER) {
              return;
            }

            const summaryStep = stepUtils.stepNumberForKey(
              authorization.steps,
              AuthorizationStepKey.Summary
            );

            const authRoute = `${getAuthorizationRoute(
              authorization
            )}?step=${summaryStep}&id=${authorization.id}`;

            return (
              <Button
                onClick={
                  cardPlacement === CARD_PLACEMENT.PATIENT_TREATMENT_DETAIL
                    ? () =>
                        window.open(
                          `${window.location.origin}/#${authRoute}`,
                          "_blank"
                        )
                    : () => history.push(`${authRoute}`)
                }
                variant="contained"
                sx={{
                  borderRadius: "20px",
                  backgroundColor: buttonBackground,
                  "&:hover": { backgroundColor: theme.palette.error.main },
                }}
              >
                View details
              </Button>
            );
          };

          return (
            <ActionBanner
              backgroundColor={backgroundColor}
              color={color}
              button={button()}
              subtitle={subtitle}
              dataCy="actionRequiredBanner"
              title={title}
              icon={icon}
              cardPlacement={cardPlacement}
            />
          );
        })()}
    </>
  );
};
